// src/components/layout/LangRoute.jsx
import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadLocale, isSupportedLang } from '../../i18n';

/** Fallback language when :lang is invalid or page doesn't support other languages. Always English. */
const FALLBACK_LANG = 'en';

/**
 * Wrapper for routes under /:lang. Validates :lang, loads locale, syncs i18n, sets <html lang>, renders Outlet.
 */
export default function LangRoute() {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  const normalizedLang = (lang || '').toLowerCase();
  const isValid = isSupportedLang(normalizedLang);

  useEffect(() => {
    if (!isValid) {
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await loadLocale(normalizedLang);
        if (cancelled) return;
        if (i18n.language !== normalizedLang) {
          await i18n.changeLanguage(normalizedLang);
        }
        if (typeof document !== 'undefined' && document.documentElement) {
          document.documentElement.lang = normalizedLang;
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [normalizedLang, isValid, i18n]);

  if (!isValid) {
    // Always fallback to English for invalid/unsupported lang
    const to = `/${FALLBACK_LANG}${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={to} replace />;
  }

  // During react-snap pre-rendering, locale loading is async and the page would be
  // captured in the loading state. Render the Outlet immediately in headless Chrome.
  const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('HeadlessChrome');
  if (!isPrerendering && (error || !ready)) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    );
  }

  return <Outlet />;
}
